import {Button, Container, Group, NumberInput, Stack, Stepper, Text, Title} from '@mantine/core';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe, StripeElementsOptions} from '@stripe/stripe-js';
import {PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {CustomerSelector} from '../components/CustomerSelector';
import {PriceSelector} from '../components/PriceSelector';
import {FlowResultDisplay} from '../components/FlowResultDisplay';
import {FlowResult, extractErrorMessage} from '../types';
import {add as createSetupIntent} from '../../setup-intents/services/SetupIntentsService';
import {add as createSubscription} from '../../subscriptions/services/SubscriptionsService';
import {SetupIntent} from '../../setup-intents/types';
import {Subscription} from '../../subscriptions/types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import config from '../../../config';

const stripePromise = loadStripe(config.stripe.publishableKey);

const SetupForm = ({onResult}: { onResult: (result: FlowResult) => void }) => {
    const stripe = useStripe();
    const elements = useElements();

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements) return;

        const {error, setupIntent} = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: 'if_required',
        });

        if (error) {
            onResult({success: false, error: error.message});
        } else {
            onResult({
                success: true,
                intentId: setupIntent?.id,
                paymentMethodId: setupIntent?.payment_method as string,
                status: setupIntent?.status,
            });
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement/>
            <Button my="sm" type="submit" disabled={!stripe}>
                Save Payment Method & Subscribe
            </Button>
        </form>
    );
};

export const SubscriptionFlowPage = () => {
    const navigate = useNavigate();
    const [active, setActive] = useState(0);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [priceId, setPriceId] = useState<string | null>(null);
    const [quantity, setQuantity] = useState<number>(1);
    const [setupIntent, setSetupIntent] = useState<SetupIntent | null>(null);
    const [creating, setCreating] = useState(false);
    const [result, setResult] = useState<FlowResult | null>(null);

    const handleCreateSetupIntent = async () => {
        if (!customerId) return;
        setCreating(true);
        try {
            const si = await createSetupIntent({
                customerId,
                paymentMethodTypes: ['card'],
                usage: 'offSession',
            });
            console.log('Setup intent created:', si);
            setSetupIntent(si as SetupIntent);
            setActive(1);
        } catch (e) {
            notifyError(e);
        } finally {
            setCreating(false);
        }
    };

    const handleSetupResult = async (flowResult: FlowResult) => {
        if (!flowResult.success || !flowResult.paymentMethodId || !customerId || !priceId) {
            setResult(flowResult);
            setActive(3);
            return;
        }

        setActive(2);
        setCreating(true);

        try {
            const sub = await createSubscription({
                customerId,
                defaultPaymentMethod: flowResult.paymentMethodId,
                items: [{price: priceId, quantity}],
            }) as Subscription;

            notifySuccess('Subscription created');

            setResult({
                success: true,
                intentId: flowResult.intentId,
                paymentMethodId: flowResult.paymentMethodId,
                subscriptionId: sub?.id,
                status: sub?.status,
            });
            setActive(3);
        } catch (e) {
            notifyError(e);
            setResult({
                success: false,
                intentId: flowResult.intentId,
                paymentMethodId: flowResult.paymentMethodId,
                error: extractErrorMessage(e),
            });
            setActive(3);
        } finally {
            setCreating(false);
        }
    };

    const handleReset = () => {
        setActive(0);
        setCustomerId(null);
        setPriceId(null);
        setQuantity(1);
        setSetupIntent(null);
        setResult(null);
    };

    const options = setupIntent?.clientSecret ? {
        clientSecret: setupIntent.clientSecret,
        appearance: {
            theme: 'stripe',
            rules: {
                '.Input': {
                    border: '1px solid #E0E6EB',
                },
            },
        },
    } as StripeElementsOptions : undefined;

    return (
        <Container size="sm" py="xl">
            <Stack gap="xl">
                <div>
                    <Title order={3}>Subscription Flow</Title>
                    <Text c="dimmed" size="sm">
                        Save a payment method, then create a subscription using it.
                    </Text>
                </div>

                <Stepper active={active} allowNextStepsSelect={false}>
                    <Stepper.Step label="Customer & Price"/>
                    <Stepper.Step label="Payment Details"/>
                    <Stepper.Step label="Creating Subscription" loading={creating}/>
                    <Stepper.Step label="Result"/>
                </Stepper>

                {active === 0 && (
                    <Stack gap="md">
                        <CustomerSelector value={customerId} onChange={setCustomerId}/>
                        <PriceSelector value={priceId} onChange={setPriceId} recurringOnly/>
                        <NumberInput
                            label="Quantity"
                            value={quantity}
                            onChange={v => setQuantity(Number(v) || 1)}
                            min={1}
                        />
                        <Group justify="space-between">
                            <Button variant="subtle" onClick={() => navigate('/payment-flow-tester')}>
                                Back
                            </Button>
                            <Button
                                onClick={handleCreateSetupIntent}
                                disabled={!customerId || !priceId}
                                loading={creating}
                            >
                                Continue to Payment
                            </Button>
                        </Group>
                    </Stack>
                )}

                {active === 1 && options && (
                    <Elements stripe={stripePromise} options={options}>
                        <SetupForm onResult={handleSetupResult}/>
                    </Elements>
                )}

                {active === 2 && (
                    <Stack gap="md" align="center">
                        <Text>Creating subscription with saved payment method...</Text>
                    </Stack>
                )}

                {active === 3 && result && (
                    <FlowResultDisplay result={result} onReset={handleReset}/>
                )}
            </Stack>
        </Container>
    );
};
